import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";
import DiagramRenderer from "@/components/DiagramRenderer";
import MomentBanner from "@/components/MomentBanner";

/**
 * Question renderer. Reads only public question fields from the engine.
 * `option_labels` is NEVER rendered (engine doesn't return it).
 *
 * Rendering modes — driven by question.answer_kind set by the backend:
 *   - "true_false" : two large True / False tap buttons (engine surfaces
 *                    ["True","False"] in question.options).
 *   - "mcq"        : tap-list of options (for ex-TITA word answers that
 *                    ship with options, plus all real MCQ Qs).
 *   - "numeric"    : numeric keypad ONLY (digits, ., /, -, ⌫, Clear) for
 *                    pure-numeric Math TITA. No free text.
 *   - "n/a"        : standard MCQ path (answer_format === "MCQ").
 * Skipped questions (data-issue: TITA without options on non-numeric / non-Math)
 * never reach the UI — the engine filters them out at selection time.
 */
export default function QuestionCard({ question, onSubmit, layerLabel = "Practice", progress, moment }) {
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");
  const [startTs] = useState(() => Date.now());

  useEffect(() => {
    setSelected(null);
    setText("");
  }, [question?.record_id]);

  if (!question) return null;

  // Resolve render mode. MCQ format is its own classic path; everything else
  // is driven off the engine's `answer_kind`.
  const kind = question.answer_kind || (question.answer_format === "MCQ" ? "n/a" : "numeric");
  const isMcqClassic   = question.answer_format === "MCQ";
  const isTrueFalse    = kind === "true_false";
  const isOptionsKind  = isMcqClassic || isTrueFalse || kind === "mcq";
  const isNumeric      = kind === "numeric";

  const submit = () => {
    const answer = isOptionsKind ? selected : text.trim();
    if (!answer) return;
    const seconds = (Date.now() - startTs) / 1000;
    onSubmit(answer, seconds);
  };

  return (
    <div className="fade-up max-w-3xl mx-auto" data-testid="question-card">
      {/* Gamification Moment Banner */}
      <MomentBanner moment={moment} className="mb-4" />
      
      <div className="flex items-center justify-between mb-4">
        <span className="px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wider" data-testid="layer-tag">
          {layerLabel}
        </span>
        {progress && (
          <span className="text-xs font-semibold text-slate-500" data-testid="question-progress">
            Q {progress.current} / {progress.total}
          </span>
        )}
      </div>

      <Card className="rounded-3xl p-8 md:p-10 mit-shadow border-sky-100 bg-white">
        {question.passage_text && (
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5" data-testid="passage-text">
            <div className="text-xs uppercase font-semibold text-amber-700 mb-2">Passage</div>
            <p className="text-slate-700 text-base leading-relaxed">{question.passage_text}</p>
          </div>
        )}

        {question.has_diagram && (
          <DiagramRenderer recordId={question.record_id} description={question.diagram_description} />
        )}

        <p className="font-display text-xl md:text-2xl text-slate-800 mb-7 leading-snug" data-testid="question-text">
          {question.question_text}
        </p>

        {/* ── True / False — two big tap buttons ─────────────────────────── */}
        {isTrueFalse ? (
          <div className="grid grid-cols-2 gap-4" data-testid="true-false-buttons">
            {["True", "False"].map((opt) => {
              const active = selected === opt;
              const accent = opt === "True"
                ? (active ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50")
                : (active ? "border-rose-400    bg-rose-50    text-rose-900"    : "border-slate-200 bg-white text-slate-700 hover:border-rose-300    hover:bg-rose-50");
              const Icon = opt === "True" ? CheckCircle2 : XCircle;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSelected(opt)}
                  className={`flex flex-col items-center justify-center gap-2 px-5 py-8 rounded-3xl border-2 transition-colors tap-44 text-xl font-bold ${accent}`}
                  data-testid={`tf-btn-${opt.toLowerCase()}`}
                  aria-pressed={active}
                >
                  <Icon className="w-8 h-8" />
                  {opt}
                </button>
              );
            })}
          </div>
        ) : isOptionsKind ? (
          /* ── MCQ — classic MCQ OR ex-TITA word answers with options ───── */
          <div className="space-y-3" role="radiogroup" data-testid="options-list">
            {(question.options || []).map((opt, i) => {
              const active = selected === opt;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelected(opt)}
                  className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-colors tap-44 text-base md:text-lg font-medium ${active ? "border-sky-400 bg-sky-50 text-slate-900" : "border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50"}`}
                  data-testid={`option-btn-${i}`}
                  aria-pressed={active}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        ) : isNumeric ? (
          /* ── Numeric keypad ONLY — pure-numeric Math TITA ───────────── */
          <div data-testid="tita-input-area">
            <input
              type="text"
              value={text}
              readOnly
              placeholder="Tap the keypad below"
              className="w-full text-2xl font-bold text-center px-5 py-4 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:outline-none"
              data-testid="tita-input"
              inputMode="none"
            />
            <div className="grid grid-cols-5 gap-2 mt-4 max-w-md mx-auto" data-testid="numeric-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0, ".", "/", "-"].map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setText(text + k)}
                  className="py-3 rounded-xl bg-sky-50 hover:bg-sky-100 text-slate-800 font-semibold tap-44"
                  data-testid={`keypad-${k}`}
                >
                  {k}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setText(text.slice(0, -1))}
                className="py-3 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold col-span-2 tap-44"
                data-testid="keypad-back"
              >
                ⌫
              </button>
              <button
                type="button"
                onClick={() => setText("")}
                className="py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold col-span-2 tap-44"
                data-testid="keypad-clear"
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          /* ── Fallback — shouldn't happen since engine filters "skip" Qs ── */
          <div className="text-center text-rose-600 text-sm font-semibold py-4" data-testid="render-error">
            This question is missing data and cannot be rendered. Please ask your teacher.
          </div>
        )}

        <div className="mt-7 flex justify-end">
          <Button
            onClick={submit}
            disabled={isOptionsKind ? !selected : !text.trim()}
            className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-6 tap-44"
            data-testid="submit-answer-btn"
          >
            Submit answer
          </Button>
        </div>
      </Card>
    </div>
  );
}
